import {
  BrightyListItem,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  HookForm,
  List,
  Stack,
  Typography,
} from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { ApiPayout } from '../../../openapi';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';
import { useExecutePayoutMutation } from '../../../api/payouts';
import { OtpField } from '../../Field/OtpField';
import { useForm } from 'react-hook-form';
import { useOtpResendMutation } from '../../../api/auth';
import { addSeconds } from 'date-fns';
import { useFormError } from '../../../hooks/useFormError';

type FormValues = {
  otp?: string;
};

export function ConfirmPayoutDialog({
  payout,
  open,
  onClose,
}: {
  payout: ApiPayout;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const form = useForm({});
  const idempotencyKey = useIdempotencyKey();
  const [resendAllowedAt, setResendAllowedAt] = useState<Date | undefined>(undefined);
  const [destination, setDestination] = useState<string | undefined>(undefined);
  const mutation = useExecutePayoutMutation({
    payoutId: payout.id,
    createdAt: payout.createdAt,
    idempotencyKey,
  });
  const otpResendMutation = useOtpResendMutation();

  const handleOtpResend = useCallback(() => {
    if (mutation.data?.type === 'CHALLENGE_REQUIRED' && mutation.data.otp) {
      otpResendMutation.mutate({
        questId: mutation.data.questId,
      });
    }
  }, [mutation.data, otpResendMutation]);

  const handleSubmit = useCallback(
    (values: FormValues) => {
      const otp = values.otp;
      if (typeof otp === 'string') {
        mutation.mutate({ solution: otp });
      } else {
        mutation.mutate({});
      }
    },
    [mutation],
  );

  const handleClose = useCallback(() => {
    if (!mutation.isPending) {
      onClose();
      mutation.reset();
      form.reset();
    }
  }, [mutation, onClose, form]);

  useEffect(() => {
    if (mutation.data?.type === 'CHALLENGES_COMPLETED') {
      onClose();
    }
  }, [mutation, onClose]);

  useEffect(() => {
    if (mutation.data?.type === 'CHALLENGE_REQUIRED' && mutation.data.otp) {
      setDestination(mutation.data.otp.destination);
      setResendAllowedAt(addSeconds(new Date(), mutation.data.otp.resendInSec));
    }
  }, [mutation.data]);

  useEffect(() => {
    if (otpResendMutation.data) {
      setResendAllowedAt(addSeconds(new Date(), otpResendMutation.data.resendInSec));
    }
  }, [otpResendMutation.data, otpResendMutation.submittedAt]);

  useFormError(form, 'otp', mutation.error);

  return (
    <Dialog open={open} fullWidth maxWidth="sm" onClose={handleClose}>
      <DialogTitle>{t('Are you sure you want to start the payout?')}</DialogTitle>
      <HookForm form={form} onSubmit={handleSubmit}>
        <DialogContent>
          {mutation.isIdle ? (
            <List>
              <BrightyListItem title={payout?.name} />
            </List>
          ) : (
            <Stack spacing={4}>
              <Typography variant="body1">
                {t('We sent a code to {{destination}}. Enter it here to confirm your transfer.', {
                  destination,
                })}
              </Typography>
              <OtpField
                name="otp"
                label={t('OTP code')}
                fullWidth
                required
                autoFocus
                resendAllowedAt={resendAllowedAt}
                onResend={handleOtpResend}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button autoFocus color="secondary" onClick={handleClose}>
            {t('Cancel')}
          </Button>
          <Button loading={mutation.isPending} type="submit">
            {t('Confirm and pay')}
          </Button>
        </DialogActions>
      </HookForm>
    </Dialog>
  );
}
